import { z } from 'zod';
import { moderationSummarySchema } from '../modules/moderation';

export const jobKindSchema = z.enum(['image', 'video', 'audio']);
export type JobKind = z.infer<typeof jobKindSchema>;

export const jobStatusSchema = z.enum(['queued', 'running', 'succeeded', 'failed', 'canceled']);
export type JobStatus = z.infer<typeof jobStatusSchema>;

export const jobOutputAssetSchema = z.object({
  assetUrl: z.string().url(),
  thumbnailUrl: z.string().url().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  storagePath: z.string().optional(),
  storageBucket: z.string().optional(),
});
export type JobOutputAsset = z.infer<typeof jobOutputAssetSchema>;

export const jobOutputSchema = z.object({
  assetUrl: z.string().url(),
  thumbnailUrl: z.string().url().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  storagePath: z.string().optional(),
  storageBucket: z.string().optional(),
  assets: z.array(jobOutputAssetSchema).optional(),
});
export type JobOutput = z.infer<typeof jobOutputSchema>;

export const jobAssetSchema = z.object({
  field: z.string(),
  storagePath: z.string(),
  storageBucket: z.string().optional(),
  url: z.string().url().optional(),
  index: z.number().int().nonnegative().optional(),
});
export type JobAsset = z.infer<typeof jobAssetSchema>;

export const storyboardJobPhaseSchema = z.enum([
  'analysis',
  'anchor',
  'scene_image',
  'scene_video',
  'stitch',
  'pdf',
]);
export type StoryboardJobPhase = z.infer<typeof storyboardJobPhaseSchema>;

export const storyboardJobContextSchema = z
  .object({
    storyboardId: z.string(),
    phase: storyboardJobPhaseSchema,
    sceneId: z.string().optional(),
    sceneOrder: z.number().int().optional(),
    totalScenes: z.number().int().optional(),
  })
  .nullable();
export type StoryboardJobContext = z.infer<typeof storyboardJobContextSchema>;

export const assistantJobContextSchema = z
  .object({
    sessionId: z.string(),
    runId: z.string().optional(),
  })
  .nullable();
export type AssistantJobContext = z.infer<typeof assistantJobContextSchema>;

export const jobSchema = z.object({
  id: z.string(),
  kind: jobKindSchema,
  status: jobStatusSchema,
  prompt: z.string().min(1),
  model: z.string().min(1),
  createdBy: z.string().min(1),
  orgId: z.string().optional(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
  startedAt: z.string().datetime().optional(),
  completedAt: z.string().datetime().optional(),
  provider: z
    .object({
      name: z.string(),
      model: z.string(),
      version: z.string().optional(),
      rateLimitKey: z.string().optional(),
      statusUrl: z.string().url().optional(),
      responseUrl: z.string().url().optional(),
      cancelUrl: z.string().url().optional(),
      cancelStatus: z.string().optional(),
    })
    .optional(),
  cost: z.number().optional(),
  predictionId: z.string().optional(),
  cancelRequestedAt: z.string().datetime().optional(),
  clientId: z.string().optional(),
  projectId: z.string().optional(),
  error: z
    .object({
      code: z.string(),
      message: z.string(),
      rootCause: z.string().optional(),
    })
    .optional(),
  output: jobOutputSchema.optional(),
  settings: z.record(z.string(), z.unknown()).default({}),
  assets: z.array(jobAssetSchema).optional(),
  moderation: moderationSummarySchema.optional(),
  storyboardContext: storyboardJobContextSchema.optional(),
  assistantContext: assistantJobContextSchema.optional(),
});
export type Job = z.infer<typeof jobSchema>;

export const jobTimelineEventSchema = z.object({
  id: z.string(),
  jobId: z.string(),
  status: jobStatusSchema,
  message: z.string(),
  createdAt: z.string().datetime(),
});
export type JobTimelineEvent = z.infer<typeof jobTimelineEventSchema>;
